import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import {
  campaignAction as campaignActionApi,
  dealAction as dealActionApi,
  fetchCampaignDetail,
  startThread,
} from "./services/brandModeServices";
import { EMPTY_VALUE, fullName } from "./brandUtils";
import { dealAction, timeAgo } from "./brandDealUtils";
import ContractChoiceModal from "./modals/ContractChoiceModal";
import ContractTermsModal from "./modals/ContractTermsModal";
import {
  ActionBar,
  BackLink,
  Badge,
  BmModal,
  Btn,
  Card,
  ConfirmModal,
  ErrorState,
  Loading,
  MetricCard,
  Overline,
  StatusBadge,
} from "./ui/BrandUI";

const STATUS_DOT = {
  draft: "#b8b8bc",
  open: "#2f6fed",
  in_contract: "#2f6fed",
  in_progress: "#1f9d55",
  completed: "#1f9d55",
  archived: "#b8b8bc",
  cancelled: "#e2241a",
};

const CampaignManagePage = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { refreshBadges, refreshCampaigns } = useBrandMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [contractFor, setContractFor] = useState(null); // deal
  const [contractMode, setContractMode] = useState(null); // 'standard' | 'custom'
  const [reasonFor, setReasonFor] = useState(null); // deal
  const [confirm, setConfirm] = useState(null); // 'archive' | 'cancel'
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = async () => {
    try {
      const payload = await fetchCampaignDetail(campaignId);
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load campaign");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const campaign = data?.campaign;
  const roster = useMemo(() => data?.roster || [], [data]);
  const counts = campaign?.counts || {};
  const uncontacted = useMemo(() => roster.filter((r) => r.status === "draft"), [roster]);
  const contacted = useMemo(() => roster.filter((r) => r.status !== "draft"), [roster]);

  const openMessages = async (athleteId) => {
    try {
      const thread = await startThread(campaignId, athleteId);
      navigate(`/brand/campaigns/${campaignId}/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  const runAction = async (entry) => {
    const action = dealAction(entry);
    if (action.kind === "compose") return navigate(`/brand/campaigns/${campaignId}/offer/${entry.athleteId}`);
    if (action.kind === "link") return navigate(action.href);
    if (action.kind === "reason") return setReasonFor(entry);
    if (action.kind === "contract") return setContractFor(entry);
    if (action.kind === "followup") {
      setBusyId(entry.id);
      try {
        await dealActionApi(entry.id, "followup");
        toast.success(`Follow-up sent to ${fullName(entry.athlete)}.`);
        await load();
      } catch (err) {
        toast.error(err.message || "Could not send the follow-up");
      } finally {
        setBusyId(null);
      }
    }
    return null;
  };

  const sendContract = async (settings, upload) => {
    if (!contractFor) return;
    setBusyId(contractFor.id);
    try {
      if (upload) await dealActionApi(contractFor.id, "contract-upload", upload);
      await dealActionApi(contractFor.id, "contract-send", { contract: settings });
      toast.success(`Contract sent to ${fullName(contractFor.athlete)}.`);
      setContractFor(null);
      setContractMode(null);
      await load();
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not send the contract");
    } finally {
      setBusyId(null);
    }
  };

  const runCampaignAction = async () => {
    setConfirmBusy(true);
    try {
      await campaignActionApi(campaignId, confirm);
      toast.success(confirm === "archive" ? "Campaign archived." : "Campaign cancelled.");
      await refreshCampaigns();
      navigate("/brand/campaigns");
    } catch (err) {
      toast.error(err.message || "Could not update the campaign");
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  const contactRemaining = () => {
    if (!uncontacted.length) return;
    const ids = uncontacted.map((r) => r.athleteId);
    navigate(`/brand/campaigns/${campaignId}/offer/${ids[0]}?athletes=${ids.join(",")}`);
  };

  if (error) return <div className="py-10"><ErrorState>{error}</ErrorState></div>;
  if (!campaign) return <Loading label="Loading campaign..." />;

  const terminal = ["archived", "cancelled", "completed"].includes(campaign.status);
  const subtitle =
    campaign.status === "draft"
      ? `Draft · ${roster.length} athlete${roster.length === 1 ? "" : "s"} shortlisted · no offers sent yet`
      : `Campaign execution · ${counts.sent || 0} athlete${counts.sent === 1 ? "" : "s"} contacted`;

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to="/brand/campaigns">Back to Campaigns</BackLink>

      <Card className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-[6px]">
          <Overline>Campaign</Overline>
          <h1 className="bm-h-l text-bm-ink">{campaign.name}</h1>
          <p className="bm-body-l text-bm-muted">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone="outline" dot={STATUS_DOT[campaign.status]} className="uppercase">
            {campaign.status === "in_progress" || campaign.status === "open" || campaign.status === "in_contract" ? "Active" : campaign.statusLabel}
          </Badge>
          <span className="bm-body-s text-bm-tertiary">Last updated {timeAgo(campaign.updatedAt)}</span>
        </div>
      </Card>

      {campaign.status === "draft" && (
        <Card className="flex flex-wrap items-center justify-between gap-3 !bg-bm-subtle">
          <span className="bm-body-m text-bm-muted">
            This campaign is still a draft. Sending offers moves it into deal management and notifies athletes.
          </span>
          <Btn variant="secondary" size="sm" onClick={() => navigate(`/brand/campaigns/${campaignId}/edit`)}>
            Edit in builder
          </Btn>
        </Card>
      )}

      <div className="flex flex-wrap gap-4">
        <MetricCard
          label="Athletes contacted"
          value={counts.sent || 0}
          sub={roster.length ? `offers sent to ${counts.sent || 0} of ${roster.length}` : "no athletes yet"}
        />
        <MetricCard label="Accepted" value={counts.accepted || 0} sub="accepted and in contract" />
        <MetricCard label="Countered" value={counts.countered || 0} sub="awaiting your review" />
        <MetricCard muted label="Signed & active" value={counts.signed || 0} sub="contracts locked" />
      </div>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Overline>Athlete deals &amp; performance</Overline>
          <span className="bm-body-s text-bm-tertiary">
            Showing {roster.length} athlete{roster.length === 1 ? "" : "s"}
            {contacted.length !== roster.length ? ` · ${contacted.length} contacted` : " contacted"}
          </span>
        </div>
        {roster.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse">
              <thead>
                <tr>
                  <th className="bm-th !h-9 w-[340px]">Athlete</th>
                  <th className="bm-th !h-9 w-[140px]">Status</th>
                  <th className="bm-th !h-9 w-[220px]">Payments</th>
                  <th className="bm-th !h-9 w-[120px]">Messages</th>
                  <th className="bm-th !h-9 !text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((entry) => {
                  const action = dealAction(entry);
                  const a = entry.athlete || {};
                  return (
                    <tr key={entry.athleteId} className="h-[68px]">
                      <td className="bm-td">
                        <span className="flex flex-col gap-[2px]">
                          <span className="bm-h-xs text-bm-ink">{fullName(a) || "Athlete"}</span>
                          <span className="bm-body-s text-bm-muted">{[a.position, a.school].filter(Boolean).join(" · ")}</span>
                        </span>
                      </td>
                      <td className="bm-td">
                        <StatusBadge status={entry.status} />
                      </td>
                      <td className="bm-td bm-body-m text-bm-ink">
                        {entry.paymentsText || <span className="text-bm-tertiary">{EMPTY_VALUE}</span>}
                      </td>
                      <td className="bm-td">
                        {entry.unread ? (
                          <span className="flex items-center gap-2 bm-label-xs text-bm-critical">
                            <span className="h-2 w-2 rounded-full bg-bm-critical" />
                            {entry.unread} unread
                          </span>
                        ) : (
                          <span className="bm-body-m text-bm-tertiary">{EMPTY_VALUE}</span>
                        )}
                      </td>
                      <td className="bm-td">
                        <span className="flex items-center justify-end gap-3">
                          <Btn
                            variant="secondary"
                            size="sm"
                            disabled={busyId === entry.id || (terminal && action.kind !== "link" && action.kind !== "reason")}
                            onClick={() => runAction(entry)}
                          >
                            {busyId === entry.id ? "Working..." : action.label}
                          </Btn>
                          <button type="button" onClick={() => openMessages(entry.athleteId)} className="bm-label-xs text-bm-ink hover:text-bm-orange">
                            Message
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-6 text-center bm-body-m text-bm-muted">No athletes on this campaign yet. Add some in the builder.</p>
        )}
      </Card>

      <ActionBar
        subtle
        left={
          <>
            <Btn onClick={contactRemaining} disabled={!uncontacted.length || terminal}>
              {uncontacted.length ? `Contact remaining athletes (${uncontacted.length})` : "Everyone contacted"}
            </Btn>
            <Btn variant="ghost" onClick={() => navigate(`/brand/campaigns/${campaignId}/messages`)}>
              Open messages
            </Btn>
          </>
        }
        right={
          !terminal ? (
            <>
              <Btn variant="ghost" onClick={() => setConfirm("archive")}>Archive campaign</Btn>
              <button type="button" onClick={() => setConfirm("cancel")} className="bm-label-l text-bm-critical hover:underline">
                Cancel campaign
              </button>
            </>
          ) : (
            <span className="bm-body-m text-bm-muted">This campaign is {campaign.statusLabel.toLowerCase()}.</span>
          )
        }
      />

      {contractFor && !contractMode && (
        <ContractChoiceModal onChoose={setContractMode} onClose={() => setContractFor(null)} />
      )}
      {contractFor && contractMode && (
        <ContractTermsModal
          mode={contractMode}
          initial={campaign.contract}
          athleteName={fullName(contractFor.athlete)}
          busy={busyId === contractFor.id}
          onConfirm={sendContract}
          onClose={() => {
            setContractFor(null);
            setContractMode(null);
          }}
        />
      )}
      {reasonFor && (
        <BmModal title={`${fullName(reasonFor.athlete)} declined`} onClose={() => setReasonFor(null)} footer={<Btn variant="secondary" className="flex-1" onClick={() => setReasonFor(null)}>Close</Btn>}>
          <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px] bm-body-m text-bm-ink">
            {reasonFor.declineReason || "No reason was given."}
          </div>
          <p className="mt-3 bm-body-s text-bm-muted">You can still message the athlete or send a fresh offer from Discover.</p>
        </BmModal>
      )}
      {confirm && (
        <ConfirmModal
          title={confirm === "archive" ? "Archive this campaign?" : "Cancel this campaign?"}
          body={
            confirm === "archive"
              ? "Archived campaigns leave your active list but keep every deal, contract and message. You can reopen it later."
              : "Cancelling withdraws every open offer and unsigned contract. Signed deals stay in place. Athletes are notified."
          }
          ackLabel={confirm === "archive" ? "I want to archive this campaign." : "I understand open offers will be withdrawn."}
          confirmLabel={confirm === "archive" ? "Archive campaign" : "Cancel campaign"}
          tone={confirm === "archive" ? "primary" : "danger"}
          busy={confirmBusy}
          onConfirm={runCampaignAction}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default CampaignManagePage;
